import { getApiBusinessFailureMessage, isAbortLike } from '../utils/seedanceRequestUtils';

/** 项目内允许使用的 HTTP 方法；后续如果需要 PATCH/OPTIONS 之外的方法，可以在这里补充。 */
type RequestMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';

/** 响应解析方式；项目接口默认按 JSON 解析，单次请求可以覆盖为 text/blob/response 等。 */
type ResponseType = 'json' | 'text' | 'blob' | 'arrayBuffer' | 'formData' | 'response';

/** query 参数允许的基础类型，null/undefined 会在拼接时被忽略。 */
type QueryValue = string | number | boolean | null | undefined;

/** 单次请求配置。保留 fetch 原生配置，同时增加项目常用的 baseURL、params、data、token、timeout。 */
export interface RequestConfig<TData = unknown> extends Omit<RequestInit, 'body' | 'headers' | 'method'> {
  /** 请求基础路径，例如 `/proxy/base` 或某个模型配置里的 baseUrl。 */
  baseURL?: string;
  /** HTTP 方法，默认 GET。 */
  method?: RequestMethod;
  /** 自定义请求头，会和全局默认 headers 合并。 */
  headers?: HeadersInit;
  /** query 参数，会被拼接到 URL 后面。数组会展开成多个同名 query。 */
  params?: Record<string, QueryValue | QueryValue[]>;
  /** 业务请求体。普通对象会自动 JSON.stringify；FormData 等原生 body 会原样传递。 */
  data?: TData;
  /** 原生 fetch body。设置后优先级高于 data。 */
  body?: BodyInit | null;
  /** Bearer Token。传入后自动生成 `Authorization: Bearer xxx`。 */
  token?: string;
  /** 超时时间，单位毫秒。设置为 0 或负数表示不启用超时。 */
  timeout?: number;
  /** 指定响应解析方式；需要原始 Response 时传 `response`。 */
  responseType?: ResponseType;
}

/** 内部归一化后的请求配置。拦截器拿到的是这个结构，方便直接改 url/headers/method。 */
export interface NormalizedRequestConfig<TData = unknown> extends RequestConfig<TData> {
  url: string;
  method: RequestMethod;
  headers: Headers;
}

/** 响应拦截器上下文。需要根据 status/header/url 做统一处理时，从这里取。 */
export interface ResponseContext<TData = unknown> {
  url: string;
  config: NormalizedRequestConfig<TData>;
  response: Response;
}

/** 请求拦截器：适合统一加 token、trace id、租户信息，或按环境改 baseURL。 */
export type RequestInterceptor = (
  config: NormalizedRequestConfig,
) => NormalizedRequestConfig | Promise<NormalizedRequestConfig>;

/** 响应拦截器：适合统一拆包、处理登录失效、格式化数据等。 */
export type ResponseInterceptor = <T>(
  data: T,
  context: ResponseContext,
) => T | Promise<T>;

/** 全局默认配置。通过 setRequestDefaults 修改，避免散落在每个页面里。 */
interface RequestDefaults {
  baseURL?: string;
  headers?: HeadersInit;
  timeout?: number;
  responseType?: ResponseType;
}

/** 请求失败时抛出的错误。调用方可以通过 status/data/response 判断具体失败原因。 */
export class RequestError<T = unknown> extends Error {
  status: number;
  data: T;
  response: Response;

  /** 创建一个带 HTTP 状态和响应数据的错误对象。 */
  constructor(message: string, response: Response, data: T) {
    super(message);
    this.name = 'RequestError';
    this.status = response.status;
    this.data = data;
    this.response = response;
  }
}

const requestInterceptors: RequestInterceptor[] = [];
const responseInterceptors: ResponseInterceptor[] = [];

/** 默认请求配置：项目里当前多数请求都是 JSON，所以默认请求和响应都按 JSON 处理。 */
let defaults: RequestDefaults = {
  timeout: 30000,
  responseType: 'json',
  headers: {
    'Content-Type': 'application/json',
  },
};

/** 判断 URL 是否已经是完整地址；完整地址不会再拼 baseURL。 */
const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url);

/** 拼接 baseURL 和 path，并处理多余或缺失的斜杠。 */
const joinUrl = (baseURL = '', url: string) => {
  if (!baseURL || isAbsoluteUrl(url)) return url;
  return `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
};

/** 把 params 拼到 URL 上；会自动跳过 null/undefined。 */
const appendParams = (url: string, params?: RequestConfig['params']) => {
  if (!params) return url;
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    const values = Array.isArray(value) ? value : [value];
    values.forEach((item) => {
      if (item === null || item === undefined) return;
      query.append(key, String(item));
    });
  });

  const queryString = query.toString();
  if (!queryString) return url;

  return `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
};

/** 判断 headers 中是否已存在某个 header，大小写不敏感。 */
const hasHeader = (headers: Headers, key: string) => {
  return Array.from(headers.keys()).some((item) => item.toLowerCase() === key.toLowerCase());
};

/** 判断请求体是否应该按 JSON 发送；FormData/Blob 等原生类型不做 JSON.stringify。 */
const isJsonBody = (body: unknown) => {
  return (
    body !== null &&
    typeof body === 'object' &&
    !(body instanceof FormData) &&
    !(body instanceof URLSearchParams) &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer)
  );
};

/** 把用户传入的配置归一化为 fetch 可用的配置，也是请求拦截器执行前的准备阶段。 */
const normalizeConfig = <TData>(
  url: string,
  config: RequestConfig<TData> = {},
): NormalizedRequestConfig<TData> => {
  // 合并默认 headers 和单次请求 headers，单次请求优先级更高。
  const headers = new Headers(defaults.headers);
  new Headers(config.headers).forEach((value, key) => headers.set(key, value));

  if (config.token) {
    headers.set('Authorization', `Bearer ${config.token}`);
  }

  let body = config.body;
  if (body === undefined && config.data !== undefined) {
    body = isJsonBody(config.data)
      ? JSON.stringify(config.data)
      : (config.data as BodyInit);
  }

  // FormData/URLSearchParams 让浏览器自己带 Content-Type，避免 boundary 丢失。
  if (body instanceof FormData || body instanceof URLSearchParams) {
    headers.delete('Content-Type');
  } else if (body !== undefined && body !== null && !hasHeader(headers, 'Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return {
    ...config,
    url: appendParams(joinUrl(config.baseURL ?? defaults.baseURL, url), config.params),
    method: config.method ?? 'GET',
    headers,
    body,
    timeout: config.timeout ?? defaults.timeout,
    responseType: config.responseType ?? defaults.responseType,
  };
};

/** 按 responseType 或 content-type 解析响应体。 */
const parseResponse = async <T>(response: Response, responseType?: ResponseType): Promise<T> => {
  if (responseType === 'response') return response as T;
  if (response.status === 204) return null as T;

  const contentType = response.headers.get('content-type') || '';

  if (responseType === 'text') return (await response.text()) as T;
  if (responseType === 'blob') return (await response.blob()) as T;
  if (responseType === 'arrayBuffer') return (await response.arrayBuffer()) as T;
  if (responseType === 'formData') return (await response.formData()) as T;
  if (responseType === 'json' || contentType.includes('application/json')) {
    const text = await response.text();
    if (!text) return null as T;

    try {
      return JSON.parse(text) as T;
    } catch {
      throw new Error(`响应 JSON 解析失败: ${text.slice(0, 120)}`);
    }
  }

  return (await response.text()) as T;
};

/** 设置全局默认请求配置；适合在应用初始化时统一设置 baseURL、headers、timeout。 */
export const setRequestDefaults = (nextDefaults: RequestDefaults) => {
  defaults = {
    ...defaults,
    ...nextDefaults,
    headers: {
      ...defaults.headers,
      ...nextDefaults.headers,
    },
  };
};

/** 注册请求拦截器，并返回取消注册函数。 */
export const addRequestInterceptor = (interceptor: RequestInterceptor) => {
  requestInterceptors.push(interceptor);
  return () => {
    const index = requestInterceptors.indexOf(interceptor);
    if (index > -1) requestInterceptors.splice(index, 1);
  };
};

/** 注册响应拦截器，并返回取消注册函数。 */
export const addResponseInterceptor = (interceptor: ResponseInterceptor) => {
  responseInterceptors.push(interceptor);
  return () => {
    const index = responseInterceptors.indexOf(interceptor);
    if (index > -1) responseInterceptors.splice(index, 1);
  };
};

/** 核心请求方法。所有便捷方法最终都会走这里。 */
export const request = async <T = unknown, TData = unknown>(
  url: string,
  config: RequestConfig<TData> = {},
): Promise<T> => {
  let nextConfig: NormalizedRequestConfig = normalizeConfig(url, config);

  // 请求发出前依次执行拦截器，后注册的拦截器会在数组靠后位置执行。
  for (const interceptor of requestInterceptors) {
    nextConfig = await interceptor(nextConfig);
  }

  // 用 AbortController 实现基础超时控制。
  const controller = new AbortController();
  let timedOut = false;
  const timeoutId =
    nextConfig.timeout && nextConfig.timeout > 0
      ? window.setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, nextConfig.timeout)
      : null;

  try {
    // 这些字段是封装层自用字段，不能直接传给 fetch。
    const { baseURL, data, params, responseType, timeout, token, url: fullUrl, ...fetchConfig } =
      nextConfig;
    
    const response = await fetch(fullUrl, {
      ...fetchConfig,
      signal: fetchConfig.signal || controller.signal,
    });
    
    const parsedData = await parseResponse<T>(response, responseType);
    
    // 非 2xx 状态统一抛 RequestError，让业务层可以用 status/data 做判断。
    if (!response.ok) {
      const message =
        typeof parsedData === 'object' && parsedData !== null
          ? (parsedData as any).error?.message ||
            (parsedData as any).message ||
            (parsedData as any).error ||
            `HTTP ${response.status}`
          : `HTTP ${response.status}`;
      throw new RequestError(String(message), response, parsedData);
    }

    // HTTP 200 但业务 success: false 时同样抛错，便于页面 toast 展示 message
    const businessError = getApiBusinessFailureMessage(parsedData);
    if (businessError) {
      throw new RequestError(businessError, response, parsedData);
    }

    let nextData = parsedData;
    const context: ResponseContext = { url: fullUrl, config: nextConfig, response };
    // 响应成功后依次执行响应拦截器，适合在这里做统一拆包。
    for (const interceptor of responseInterceptors) {
      nextData = await interceptor(nextData, context);
    }

    return nextData;
  } catch (error) {
    const logPrefix = '[request] 请求失败';
    const logContext = {
      url: nextConfig.url,
      method: nextConfig.method,
    };

    if (error instanceof RequestError) {
      console.error(logPrefix, {
        ...logContext,
        status: error.status,
        data: error.data,
      }, error);
    } else if (isAbortLike(error)) {
      // 列表重载、搜索切换、预览刷新会主动取消进行中的请求，不算失败。
      if (timedOut) {
        console.error(logPrefix, {
          ...logContext,
          timeout: nextConfig.timeout,
          reason: '请求超时',
        }, error);
      }
    } else {
      console.error(logPrefix, logContext, error);
    }

    throw error;
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
  }
};

/** GET 请求便捷方法。 */
export const get = <T = unknown>(
  url: string,
  config?: Omit<RequestConfig, 'method' | 'data' | 'body'>,
) => request<T>(url, { ...config, method: 'GET' });

/** POST 请求便捷方法。第二个参数是业务 data，会自动按 JSON 或原生 body 处理。 */
export const post = <T = unknown, TData = unknown>(
  url: string,
  data?: TData,
  config?: Omit<RequestConfig<TData>, 'method' | 'data'>,
) => request<T, TData>(url, { ...config, method: 'POST', data });

/** PUT 请求便捷方法。 */
export const put = <T = unknown, TData = unknown>(
  url: string,
  data?: TData,
  config?: Omit<RequestConfig<TData>, 'method' | 'data'>,
) => request<T, TData>(url, { ...config, method: 'PUT', data });

/** DELETE 请求便捷方法。命名为 del 是为了避免和 delete 关键字冲突。 */
export const del = <T = unknown>(
  url: string,
  config?: Omit<RequestConfig, 'method'>,
) => request<T>(url, { ...config, method: 'DELETE' });

/** 默认导出核心 request，方便 `import request from ...` 的写法。 */
export default request;
